import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Paper,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Box,
    Button,
    Collapse,
    Chip
} from '@mui/material';
import { Delete as DeleteIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import api from '../../../services/api';
import './styles.css';

interface Slot {
    _id: string;
    date: string;
    startTime: string;
    product: {
        _id: string;
        name: string;
    };
    maxParticipants: number;
    status: string;
}

function AdminSlotsList() {
    const navigate = useNavigate();
    const [slots, setSlots] = useState<Slot[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedDates, setExpandedDates] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        fetchSlots();
    }, []);

    const fetchSlots = async () => {
        try {
            const response = await api.get('/planning/slots/all');
            setSlots(response.data);
        } catch (error) {
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (slotId: string) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce créneau ?')) return;

        try {
            await api.delete(`/planning/slots/${slotId}`);
            setSlots(slots.filter(slot => slot._id !== slotId));
        } catch (error) {
            console.error('Erreur:', error);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const toggleDate = (date: string) => {
        setExpandedDates(prev => ({
            ...prev,
            [date]: !prev[date]
        }));
    };

    // Grouper les créneaux par date
    const slotsByDate = slots.reduce((acc, slot) => {
        if (!acc[slot.date]) {
            acc[slot.date] = [];
        }
        acc[slot.date].push(slot);
        return acc;
    }, {} as { [key: string]: Slot[] });

    // Trier les dates
    const sortedDates = Object.keys(slotsByDate).sort();

    return (
        <Box className="admin-slots-list">
            <Paper className="slots-paper">
                <Box className="header-section">
                    <Typography variant="h4" className="title">
                        Liste des créneaux ouverts
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/admin/test-slots')}
                    >
                        Créer un nouveau créneau
                    </Button>
                </Box>

                <List>
                    {sortedDates.map(date => (
                        <Box key={date}>
                            <ListItem
                                button
                                onClick={() => toggleDate(date)}
                                className="date-header"
                            >
                                <ListItemText
                                    primary={formatDate(date)}
                                    secondary={`${slotsByDate[date].length} créneau(x)`}
                                />
                                <IconButton>
                                    {expandedDates[date] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </ListItem>
                            <Collapse in={expandedDates[date]}>
                                <List component="div" disablePadding>
                                    {slotsByDate[date].map(slot => (
                                        <ListItem key={slot._id} className="slot-item">
                                            <ListItemText
                                                primary={`${slot.startTime} - ${slot.product.name}`}
                                                secondary={`Participants max: ${slot.maxParticipants}`}
                                            />
                                            <ListItemSecondaryAction>
                                                <Chip
                                                    label={slot.status}
                                                    color={slot.status === 'available' ? 'success' : 'default'}
                                                    size="small"
                                                />
                                                <IconButton
                                                    edge="end"
                                                    aria-label="delete"
                                                    onClick={() => handleDelete(slot._id)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            </Collapse>
                        </Box>
                    ))}
                </List>
            </Paper>
        </Box>
    );
}

export default AdminSlotsList;